import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, X } from "lucide-react";

export function RepInsightsDrawer({ open, onOpenChange, selectedRep, teamAverages, getRoleBadgeClass, customColumns = [] }) {
  if (!selectedRep) return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[450px] bg-[#0f0f10] border-white/10" />
    </Sheet>
  );

  const insights = getInsights(selectedRep, teamAverages);
  const metrics = [
    { label: "Dials", value: selectedRep.dials, avg: teamAverages.dials },
    { label: "Connects", value: selectedRep.connects, avg: teamAverages.connects },
    { label: "Sets", value: selectedRep.sets, avg: teamAverages.sets },
    { label: "Shows", value: selectedRep.shows, avg: teamAverages.shows },
    { label: "Deals", value: selectedRep.deals, avg: teamAverages.deals },
    { label: "Cash", value: selectedRep.cash, avg: teamAverages.cash, format: "currency" },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[450px] bg-[#0f0f10] border-white/10 overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle className="text-white flex items-center gap-3">
            <Avatar className="w-12 h-12">
              <AvatarImage src={selectedRep.picture} />
              <AvatarFallback className="bg-emerald-700 text-white">{selectedRep.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                {selectedRep.name}
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${getRoleBadgeClass(selectedRep.role)}`}>{selectedRep.role}</span>
              </div>
              <div className="text-sm text-gray-400 font-normal">{selectedRep.team_name}</div>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-6">
          {/* Insights */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Performance Insights</h3>
            {insights.map((insight) => (
              <div key={insight.message} className={`p-4 rounded-lg border ${
                insight.type === "success" ? "bg-green-500/10 border-green-500/20" :
                insight.type === "warning" ? "bg-yellow-500/10 border-yellow-500/20" : "bg-red-500/10 border-red-500/20"
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  {insight.type === "success" ? <CheckCircle className="w-4 h-4 text-green-400" /> :
                   insight.type === "warning" ? <AlertTriangle className="w-4 h-4 text-yellow-400" /> :
                   <X className="w-4 h-4 text-red-400" />}
                  <span className={`font-medium ${insight.type === "success" ? "text-green-400" : insight.type === "warning" ? "text-yellow-400" : "text-red-400"}`}>
                    {insight.message}
                  </span>
                </div>
                <p className="text-sm text-gray-400 ml-6">{insight.detail}</p>
              </div>
            ))}
            {insights.length === 0 && (
              <div className="p-4 rounded-lg bg-white/5 text-center text-gray-400">Not enough data to generate insights</div>
            )}
          </div>

          {/* KPI vs Average */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">KPI vs Team Average</h3>
            <div className="space-y-4">
              {metrics.map((metric) => {
                const percentage = metric.avg > 0 ? (metric.value / metric.avg * 100) : 0;
                const isAbove = metric.value >= metric.avg;
                return (
                  <div key={metric.label} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">{metric.label}</span>
                      <div className="flex items-center gap-2">
                        <span className={isAbove ? "text-green-400" : "text-red-400"}>
                          {metric.format === "currency" ? `$${metric.value?.toLocaleString()}` : metric.value}
                        </span>
                        <span className="text-gray-500">/ {metric.format === "currency" ? `$${metric.avg?.toLocaleString()}` : metric.avg} avg</span>
                        {isAbove ? <TrendingUp className="w-4 h-4 text-green-400" /> : <TrendingDown className="w-4 h-4 text-red-400" />}
                      </div>
                    </div>
                    <Progress value={Math.min(percentage, 200)} className="h-2 bg-white/10" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Activity Summary */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Activity Summary</h3>
            <div className="grid grid-cols-2 gap-3">
              <SummaryCard label="Show Rate" value={selectedRep.show_percent === null || selectedRep.show_percent === undefined ? "—" : `${selectedRep.show_percent}%`} />
              <SummaryCard label="Close Rate" value={selectedRep.close_percent === null || selectedRep.close_percent === undefined ? "—" : `${selectedRep.close_percent}%`} />
              <SummaryCard label="Total Cash" value={`$${selectedRep.cash?.toLocaleString()}`} color="text-emerald-400" />
              <SummaryCard label="Deals" value={selectedRep.deals} color="text-orange-400" />
            </div>
          </div>

          {/* Custom EOD-template columns (manager-pinned) */}
          {customColumns.length > 0 && (
            <div className="space-y-3" data-testid="rep-insights-custom-columns">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Custom Metrics</h3>
              <div className="grid grid-cols-2 gap-3">
                {customColumns.map((col) => {
                  const raw = selectedRep.custom_values?.[col.key] ?? 0;
                  const display = col.format === "currency"
                    ? `$${Number(raw).toLocaleString()}`
                    : col.format === "percent"
                      ? `${Number(raw)}%`
                      : Number(raw).toLocaleString();
                  return (
                    <SummaryCard
                      key={col.key}
                      label={col.label}
                      value={display}
                      color="text-emerald-300"
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SummaryCard({ label, value, color = "text-white" }) {
  return (
    <div className="bg-white/5 rounded-lg p-4">
      <div className="text-xs text-gray-500 uppercase mb-1">{label}</div>
      <div className={`stat-value text-2xl ${color}`}>{value}</div>
    </div>
  );
}

function getInsights(rep, teamAverages) {
  const insights = [];
  if (rep.show_percent >= 70) insights.push({ type: "success", message: "Strong show rate", detail: `${rep.show_percent}% of calls resulted in shows - above team average` });
  else if (rep.show_percent < 40 && rep.shows > 3) insights.push({ type: "warning", message: "Low show rate", detail: `Only ${rep.show_percent}% show rate - consider follow-up improvements` });

  const connectToSet = rep.connects > 0 ? (rep.sets / rep.connects * 100) : 0;
  if (connectToSet < 20 && rep.connects > 5) insights.push({ type: "danger", message: "Low connect-to-set", detail: `${connectToSet.toFixed(1)}% of connects converted to sets - needs improvement` });
  else if (connectToSet >= 50) insights.push({ type: "success", message: "Great connect-to-set", detail: `${connectToSet.toFixed(1)}% conversion rate from connects to sets` });

  const totalActivity = rep.dials + rep.dms;
  if (totalActivity < teamAverages.dials * 0.5 && totalActivity < 50) insights.push({ type: "danger", message: "Low activity", detail: `${totalActivity} total outreach vs ${teamAverages.dials} team average` });
  else if (totalActivity > teamAverages.dials * 1.5) insights.push({ type: "success", message: "High activity", detail: `${totalActivity} total outreach - leading the team!` });

  if (rep.close_percent >= 40) insights.push({ type: "success", message: "Strong closer", detail: `${rep.close_percent}% close rate - excellent performance` });
  else if (rep.close_percent < 20 && rep.shows > 3) insights.push({ type: "warning", message: "Close rate needs work", detail: `${rep.close_percent}% close rate - review sales process` });

  return insights;
}
